import useQueryParams from '../hooks/useQueryParams';
import { useQuery } from '@tanstack/react-query';
import { getToken } from '../utils/auth';
import { useState } from 'react';

import ReportControls from './controls/ReportControls';
import metricsMap from '../utils/metrics-map';

import Papa from 'papaparse';

import {
  PlusIcon,
  MinusIcon,
  ExclamationTriangleIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';

const { VITE_API_SERVICE } = import.meta.env;

/**
 * Formats a date string into a localized format based on granularity
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted date string
 */
function formatDate(dateString) {
  const granularity = window.location.search
    ? new URLSearchParams(window.location.search).get('granularity') || 'DAY'
    : 'DAY';

  const pad = (num) => String(num).padStart(2, '0');

  const cleanDate = dateString.split('+')[0];

  // Parse ISO date string
  const [datePart, timePart] = cleanDate.split('T');
  const [year, month, day] = datePart.split('-');

  let formattedDate = `${pad(month)}/${pad(day)}/${year}`;

  if (granularity === 'HOUR' && timePart) {
    const [hours, minutes, seconds] = timePart.split(':');
    formattedDate += ` ${pad(hours)}:${pad(minutes)}:${pad(seconds.split('.')[0])}`;
  }

  return formattedDate;
}

/**
 * Formats Range for Date
 * @param {string} dateString - The date string to format
 * @returns {string} The formatted date string
 */
function formatRange(dateString) {
  const granularity = window.location.search
    ? new URLSearchParams(window.location.search).get('granularity') || 'DAY'
    : 'DAY';

  if (granularity === 'DAY') {
    return formatDate(dateString.start);
  }

  return `${formatDate(dateString.start)} - ${formatDate(dateString.end)}`;
}

/**
 * Formats a value based on its type and key name
 * @param {string} key - The key name of the value
 * @param {number|string|null|undefined} value - The value to format
 * @returns {string} The formatted value as a string
 */
function formatValue(key, value) {
  if (!value && value !== 0) {
    return '-';
  }

  if (typeof value === 'number') {
    if (key === 'supplyRevenueInUSD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }

    if (key === 'inventoryAddressabilityRate' || key === 'fillRate') {
      return `${value.toFixed(2)}%`;
    }

    return value.toLocaleString('en-US');
  }

  return value;
}

/**
 * Finds the display name and value in a data object at any level
 * @param {Object} data - The data object to search
 * @returns {Object|null} The display name and value object or null if not found
 */
function findDisplayInfo(data) {
  if (!data?.total) return null;

  // Check for common display fields
  const displayFields = [
    'geoCountryName',
    'publisherName',
    'zonePosition',
    '__time'
  ];
  for (const field of displayFields) {
    if (field in data.total) {
      return {
        name:
          field === '__time'
            ? `${formatRange(data.total[field])}`
            : data.total[field],
        value: data.total[field]
      };
    }
  }

  // Check in data array if it exists
  if (Array.isArray(data.data)) {
    for (const item of data.data) {
      const info = findDisplayInfo(item);
      if (info) return info;
    }
  }

  return null;
}

/**
 * Main component for displaying and managing a custom report with expandable rows
 * Shows loading states, error handling, and tabular data with collapsible publisher details
 * @returns {JSX.Element} The rendered CustomReport component
 */
export default function CustomReport() {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedTimeSections, setExpandedTimeSections] = useState(new Set());
  const [shouldFetch, setShouldFetch] = useState(false);
  const [sortOrder, setSortOrder] = useState('ASC');
  const [sortBy, setSortBy] = useState('');

  const { getQueryString, setBulkQueryParams, getParams } = useQueryParams();

  /**
   * Handles column sorting when a column header is clicked
   * @param {string} column - The column to sort by
   */
  function handleSort(column) {
    const newSortOrder =
      sortBy === column && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(column);
    setSortOrder(newSortOrder);

    const currentParams = getParams();
    setBulkQueryParams({
      ...currentParams,
      sortBy: column,
      sortOrder: newSortOrder
    });

    if (shouldFetch) {
      refetch();
    }
  }

  /**
   * Renders the sort icon based on current sort state
   * @param {string} column - The column to check
   * @returns {JSX.Element|null} The sort icon component
   */
  function renderSortIcon(column) {
    if (sortBy !== column) {
      return (
        <ChevronUpDownIcon className="inline-block opacity-50 ml-1 w-4 h-4" />
      );
    }
    return sortOrder === 'ASC' ? (
      <ChevronUpIcon className="inline-block ml-1 w-4 h-4" />
    ) : (
      <ChevronDownIcon className="inline-block ml-1 w-4 h-4" />
    );
  }

  /**
   * Toggles the expansion state of a row at the given index
   * @param {number} id - The index of the row to toggle
   */
  function toggleRow(id) {
    setExpandedRows(function (prev) {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  /**
   * Toggles the expansion state of a time section at the given index
   * @param {number} index - The index of the time section to toggle
   */
  function toggleTimeSection(index) {
    setExpandedTimeSections(function (prev) {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }

  function toggleAllTimeSections(data) {
    setExpandedTimeSections(function (prev) {
      // If all time sections are currently expanded, collapse all
      if (prev.size === data?.report?.data?.length) {
        return new Set();
      }
      // Otherwise, expand all time sections
      return new Set(
        data?.report?.data?.map(function (_, index) {
          return index;
        }) || []
      );
    });
  }

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['custom-report'],
    queryFn: async function () {
      const token = getToken();

      const response = await fetch(
        `${VITE_API_SERVICE}/v1/reports/query?${getQueryString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error(data.message);
        }

        if (response.status === 401) {
          throw new Error(data.message);
        }

        throw new Error('Failed to fetch custom report');
      }

      return data;
    },
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false
  });

  function handleExportCSV() {
    if (!data?.report?.data) {
      console.error('No data is available to export!');
      return;
    }

    // Get columns from the total data
    const columns = Object.keys(data.report?.total || {}).filter((key) => {
      return key !== '__time' && key !== 'data';
    });

    const rows = [];

    // Header Row
    const header = ['Name'];
    columns.forEach((col) => {
      header.push(metricsMap[col] || col);
    });
    rows.push(header);

    // Total Row
    const totalRow = ['Total'];
    columns.forEach((col) => {
      totalRow.push(formatValue(col, data.report?.total[col]));
    });
    rows.push(totalRow);

    // Process each time section
    data?.report?.data.forEach((timeSection) => {
      processRow(timeSection, 0);

      // Process publishers under this time section
      timeSection.data?.forEach((publisher) => {
        processRow(publisher, 1);

        // Process zones or other child items under publishers
        publisher.data?.forEach((childItem) => {
          processRow(childItem, 2);
        });
      });
    });

    // Helper function to process rows with proper indentation
    function processRow(row, level) {
      const displayInfo = findDisplayInfo(row);
      if (!displayInfo) return;

      // Add indentation based on level
      const indent = Array(level * 2)
        .fill(' ')
        .join('');
      const rowData = [indent + displayInfo.name];

      // Add formatted values for each column
      columns.forEach((col) => {
        rowData.push(formatValue(col, row.total[col]));
      });

      rows.push(rowData);
    }

    // Generate CSV using PapaParse
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Generate filename with date range
    const currentParams = getParams();
    const filename = `${currentParams.fromDate}-${currentParams.toDate}.csv`;

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release the URL object
    URL.revokeObjectURL(url);
  }

  /**
   * Initiates the report data fetch by enabling the query and triggering a refetch
   * Used when the user clicks the Run Report button
   */
  function handleRunReport() {
    setShouldFetch(true);
    refetch();
  }

  function renderContent() {
    if (isLoading && !data) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col justify-center items-center gap-1 min-h-[400px]">
          <ExclamationTriangleIcon className="mb-3 w-12 h-12" />
          <p className="font-medium">Failed to load report data</p>
          <p className="max-w-md text-error text-sm text-base-content/60 text-center">
            {error.message}
          </p>
          <p className="max-w-md text-xs text-base-content/60 text-center">
            Hint: Try adjusting metrics or publishers selection
          </p>
        </div>
      );
    }

    if (!shouldFetch) {
      return (
        <div className="flex flex-col justify-center items-center gap-4 min-h-[400px]">
          <p className="text-gray-500">
            Set your filters above and click Run Report to view the data
          </p>
        </div>
      );
    }

    if (!data?.report?.data) {
      return (
        <div className="flex flex-col justify-center items-center gap-3 min-h-[400px]">
          <ExclamationTriangleIcon className="w-12 h-12" />
          <p className="font-medium">No data available</p>
          <p className="max-w-md text-sm text-base-content/60 text-center">
            Try adjusting metrics or publishers selection
          </p>
        </div>
      );
    }

    // Get columns from the total data
    const columns = Object.keys(data.report.total || {}).filter(function (key) {
      return key !== '__time' && key !== 'data';
    });

    return (
      <div>
        <div className="flex justify-end mb-4 bg-white">
          <button
            onClick={handleExportCSV}
            className="flex w-full sm:w-fit justify-center items-center font-semibold border-2 border-[#93C01F] text-[#93C01F] px-8 py-3
             gap-2 h-11 min-h-0 text-base rounded-full hover:bg-[#93C01F] hover:text-white transition-all ease-out duration-300"
            disabled={isFetching}
          >
            Download CSV
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-400 pb-4">
          <table
            className={`table rounded-none w-full overflow-hidden transition-opacity duration-200 ${isFetching ? 'opacity-50' : ''}`}
          >
            <thead>
              <tr>
                <th className="px-2 w-8">
                  {data.report.data.length > 0 && (
                    <button
                      className="p-0 btn btn-xs btn-ghost"
                      onClick={function () {
                        toggleAllTimeSections(data);
                      }}
                      disabled={isFetching}
                    >
                      <ChevronUpDownIcon className="w-4 h-4" />
                    </button>
                  )}
                </th>
                <th className="px-2 font-bold text-blue-900 text-sm">
                  Time, Publisher
                </th>
                {columns.map(function (column, colIndex) {
                  const isLast = colIndex === columns.length - 1;
                  return (
                    <th
                      key={column}
                      className={`text-center px-2 ${colIndex === columns.length - 1 ? 'pr-0' : 'pr-6'}`}
                    >
                      <span
                        className={`flex justify-start items-center ${colIndex === columns.length - 1 ? 'gap-8 justify-between' : 'gap-1'}`}
                      >
                        <span className="font-[600] text-gray-500">
                          {metricsMap[column]}
                        </span>
                        <button
                          className={`hover:bg-base-300 p-0 pr-1 rounded btn btn-ghost btn-xs
                            ${colIndex === columns.length - 1 ? '-ml-16' : ''}`}
                          onClick={function () {
                            handleSort(column);
                          }}
                        >
                          {renderSortIcon(column)}
                        </button>
                        {isLast && (
                          <EllipsisHorizontalIcon className="w-7 h-7 mr-4 text-gray-500" />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {/* Total Row */}
              <tr className="font-[500] text-gray-500">
                <td className="px-2"></td>
                <td className="px-2 font-bold">Total</td>
                {columns.map(function (column, colIndex) {
                  return (
                    <td
                      key={column}
                      className={`font-bold px-2 ${colIndex === columns.length - 1 ? 'pr-6' : ''}`}
                    >
                      {formatValue(column, data.report.total[column])}
                    </td>
                  );
                })}
              </tr>

              {/* Data Rows */}
              {data.report.data.map(function (row, index) {
                const displayInfo = findDisplayInfo(row);
                const isTimeExpanded = expandedTimeSections.has(index);
                const isExpanded = expandedRows.has(index);

                if (!displayInfo) return null;

                const rows = [
                  <tr
                    key={`time-${index}`}
                    onClick={function () {
                      toggleTimeSection(index);
                    }}
                    className={`cursor-pointer border border-b-gray-200 ${index % 2 === 0 ? 'bg-[#E7F5FC] hover:bg-blue-50' : 'bg-white'}`}
                  >
                    <td className="px-2 font-bold">
                      {isTimeExpanded ? (
                        <MinusIcon className="w-4 h-4" />
                      ) : (
                        <PlusIcon className="w-4 h-4" />
                      )}
                    </td>
                    <td className="px-2 font-bold">{displayInfo.name}</td>
                    {columns.map(function (column, colIndex) {
                      return (
                        <td
                          key={column}
                          className={`font-bold px-2 ${colIndex === columns.length - 1 ? 'pr-6' : ''}`}
                        >
                          {formatValue(column, row.total[column])}
                        </td>
                      );
                    })}
                  </tr>
                ];

                if (isTimeExpanded) {
                  rows.push(
                    ...row.data?.map(function (childRow, childIndex) {
                      const childDisplayInfo = findDisplayInfo(childRow);
                      if (!childDisplayInfo) return null;

                      const childRowId = `${index}-${childIndex}`;
                      const isExpanded = expandedRows.has(childRowId);

                      const childRows = [
                        <tr
                          key={`child-${index}-${childIndex}`}
                          onClick={function () {
                            toggleRow(childRowId);
                          }}
                          className={`font-[400] cursor-pointer border border-b-gray-200 ${childIndex % 2 === 0 ? 'bg-[#E7F5FC] hover:bg-blue-50' : 'bg-white'}`}
                        >
                          <td className="px-2 pl-4 w-8 font-bold">
                            {isExpanded ? (
                              <MinusIcon className="w-4 h-4" />
                            ) : (
                              <PlusIcon className="w-4 h-4" />
                            )}
                          </td>
                          <td className="px-2 font-bold">
                            <span className="pl-4">
                              {childDisplayInfo.name}
                            </span>
                          </td>
                          {columns.map(function (column, colIndex) {
                            return (
                              <td
                                key={column}
                                className={`font-bold px-2 ${colIndex === columns.length - 1 ? 'pr-6' : ''}`}
                              >
                                {formatValue(column, childRow.total[column])}
                              </td>
                            );
                          })}
                        </tr>
                      ];

                      if (isExpanded) {
                        // Add grandchild rows if they exist
                        childRow.data?.forEach(
                          function (grandchildRow, grandchildIndex) {
                            const grandchildDisplayInfo =
                              findDisplayInfo(grandchildRow);
                            if (!grandchildDisplayInfo) return;

                            childRows.push(
                              <tr
                                key={`child-${index}-${childIndex}-${grandchildIndex}`}
                                className={`${grandchildIndex % 2 === 0 ? 'bg-[#E7F5FC]' : 'bg-white'} border border-b-gray-200`}
                              >
                                <td className="px-2 w-8"></td>
                                <td className="px-2">
                                  <span className="pl-10">
                                    {grandchildDisplayInfo.name}
                                  </span>
                                </td>
                                {columns.map(function (column, colIndex) {
                                  return (
                                    <td
                                      key={column}
                                      className={`text-left px-2 ${colIndex === columns.length - 1 ? 'pr-6' : ''}`}
                                    >
                                      {formatValue(
                                        column,
                                        grandchildRow.total[column]
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          }
                        );
                      }

                      return childRows;
                    })
                  );
                }

                return rows;
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col items-start justify-center pb-6 bg-white w-full">
        <div>
          <h1 className="hidden sm:block pt-4 mb-4 sm:mb-6 font-[200] text-blue-500 text-5xl sm:text-6xl tracking-widest uppercase w-full">Report</h1>
        </div>

        <div className="flex justify-start items-center py-6 bg-white w-full">
          <div className="flex items-center justify-between w-full">
            <ReportControls
              onRunReport={handleRunReport}
              isLoading={isFetching}
            />
          </div>
        </div>


      </div>
      {renderContent()}

    </div>
  );
}
